import { useOrgConfig } from "../Common/Hooks/OrgConfigHooks";
import React, { useEffect, useState } from "react";
import withRouter from "../Common/withRouter";
import { Badge, Menu, Popover, Tooltip, Typography } from "antd";
import { CopyOutlined, TagsOutlined, FundProjectionScreenOutlined, TeamOutlined, IdcardOutlined, AppstoreOutlined, RiseOutlined, FallOutlined, SwapOutlined, DollarOutlined, ThunderboltOutlined, FileExcelOutlined } from '@ant-design/icons';
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Gauge,
  Moon,
  Sun,
  UserCircle2,
} from "lucide-react";
import { themecolor } from "../config.js";
import {
  StyleSimpleBar,
  StyledCollapsedButton,
  StyleBrandLogo,
  StyleSider,
  StyleSidebarFooter,
} from "../Common/SidebarStyle";
import { Link, useLocation } from "react-router-dom";
import utils from '../Common/utils';
import WhcLogoWhite from "../assets/images/whc_logo_white.png";
import WhcLogoBlack from "../assets/images/whc_logo_black.png";
import { useSelector, useDispatch } from "react-redux";
import { useProfile } from "../Common/Hooks/UserHooks";
import { getProfileContentPopover } from "./ProfileMenu";
import usecustomStyles from "../Common/customStyles";

const { Text } = Typography;

const customStyles = usecustomStyles();

// Stable reference: a new [] each render would rebuild the menu every time.
const EMPTY_ALERTS = Object.freeze([]);


const SidebarLayout = ({ theme, darkMode, handleToggleMode }) => {

  const { userProfile } = useProfile();

  function getItem(label, key, icon, children, type) {
    return {
      key,
      icon,
      children,
      label,
      type,
    };
  }



  const [collapsed, setCollapsed] = useState(false);
  const [items, setItems] = useState([]);

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
    const antHeaderMain = document.getElementById("antHeaderMain");
    if (antHeaderMain) {
      antHeaderMain.style.left = !collapsed ? "100px" : "260px";
    }
    const antLayoutContent = document.getElementById("antLayoutContent");
    if (antLayoutContent) {
      antLayoutContent.style.marginLeft = !collapsed ? "100px" : "260px";
    }
    const antFooterLayout = document.getElementById("antFooterLayout");
    if (antFooterLayout) {
      antFooterLayout.style.marginLeft = !collapsed ? "100px" : "260px";
    }
  };

  const location = useLocation();

  const getCurItem = () => {
    const currentPath = location.pathname.replace("/", "");
    console.log("CURPATH", currentPath)
    return currentPath || "dashboard";
  }

  const [activatedItem, setActivatedItem] = useState(() => {return getCurItem()});


  useEffect(() => {
    setActivatedItem(getCurItem())
  }, [location]);


  const toggleActivation = (key) => {
    setActivatedItem((prevActivatedItem) => (prevActivatedItem === key ? null : key));
  };

  const filterdata = useSelector((state) => state.Filterdata.data)
  const orgConfig = useOrgConfig();

  // Repeating transactions whose repetition is about to run out. The dashboard
  // shows the detail; the sidebar only has to say "there is something here",
  // so a repetition cannot expire unnoticed while the user is on another page.
  const repeatAlerts = useSelector((state) => state.WizardAlerts?.list) || EMPTY_ALERTS;
  const alertCount = repeatAlerts.length;
  // Red once something has ALREADY lapsed -- that is past a warning.
  const alertColor = repeatAlerts.some((a) => a.is_lapsed)
    ? customStyles.colorDanger
    : customStyles.colorWarning;
  const alertTitle = alertCount === 1
    ? "1 ponavljajoča transakcija se izteka"
    : `${alertCount} ponavljajočih transakcij se izteka`;

  useEffect(() => {

    const newHash = utils.getUpdatedURL();
    // console.log('NEW NAV URRLS', newHash)

    setItems([
      getItem("Meni", "Menu", null,
        [
          getItem(
            <Link to={{pathname: "/dashboard", hash: newHash}}>
              Nadzorna plošča
              {alertCount > 0 && (
                <Tooltip title={alertTitle}>
                  <span style={{ marginLeft: 8, color: alertColor, whiteSpace: "nowrap" }}>
                    <AlertTriangle size={14} style={{ verticalAlign: "-2px" }} />
                    <span style={{ marginLeft: 3, fontSize: 12, fontWeight: 600 }}>{alertCount}</span>
                  </span>
                </Tooltip>
              )}
            </Link>,
            "dashboard",
            // Collapsed, the label is hidden -- so the badge has to ride on the
            // icon instead, or the whole warning disappears with the text.
            alertCount > 0 && collapsed
              ? <Badge dot color={alertColor} offset={[2, 0]}><Gauge size={16} /></Badge>
              : <Gauge size={16} />
          ),
          getItem(<Link to={{pathname: "/intents", hash: newHash}}>Transakcije</Link>, "intents", <CopyOutlined size={16} />),
          getItem("Vpogledi", "AuthenticationGroup", null, [
            getItem(<Link to={{pathname: "/cashflow", hash: newHash}}>Denarni tok</Link>, "cashflow", <DollarOutlined size={16} />),
            getItem(<Link to={{pathname: "/insights/income_expense", hash: newHash}}>Prihodki in odhodki</Link>, "insights-income_expense", <SwapOutlined style={{transform: 'rotate(-45deg)'}} size={16} />),
            getItem(<Link to={{pathname: "/insights/expenses", hash: newHash}}>Odhodki</Link>, "insights-expenses", <FallOutlined size={16} />),
            getItem(<Link to={{pathname: "/insights/incomes", hash: newHash}}>Prihodki</Link>, "insights-incomes", <RiseOutlined size={16} />),
            ...(orgConfig.tax_enabled
              ? [getItem(<Link to={{pathname: "/insights/tax", hash: newHash}}>DDV</Link>, "tax", <DollarOutlined size={16} />)]
              : []),
          ]),
          getItem(<Link to={{pathname: "/projects", hash: newHash}}>Projekti</Link>, "projects", <FundProjectionScreenOutlined size={16} />),
          getItem("Statični podatki", "DataGroup", null, [
            getItem(<Link to={{pathname: "/companies", hash: newHash}}>Podjetja</Link>, "companies", <TeamOutlined size={16} />),
            getItem(<Link to={{pathname: "/persons", hash: newHash}}>Osebe</Link>, "persons", <IdcardOutlined size={16} />),
            getItem(<Link to={{pathname: "/categories", hash: newHash}}>Kategorije</Link>, "categories", <AppstoreOutlined size={16} />),
            getItem(<Link to={{pathname: "/project_types", hash: newHash}}>Vrste projektov</Link>, "project_types", <FundProjectionScreenOutlined size={16} />),
            getItem(<Link to={{pathname: "/tags", hash: newHash}}>Oznake</Link>, "tags", <TagsOutlined size={16} />),
            getItem(<Link to={{pathname: "/tag_rules", hash: newHash}}>Pravila za oznake</Link>, "tag_rules", <ThunderboltOutlined size={16} />),
          ]),
          getItem("Orodja", "ToolsGroup", null, [
            getItem(<Link to={{pathname: "/import", hash: newHash}}>Uvoz iz XLSX</Link>, "import", <FileExcelOutlined size={16} />),
          ]),
        ],
        "group"
      )
    ])
  }, [filterdata, orgConfig, alertCount, alertColor, alertTitle, collapsed]);


  // console.log("act", activatedItem)

  return (
    <React.Fragment>
      <StyleSider
        id="sidebar-layout"
        width={themecolor.components.Menu.verticalSidebarWidth}
        collapsed={collapsed}
        collapsedWidth="100"
        breakpoint="lg"
      >
        <StyleBrandLogo className="demo-logo ant-mx-auto">
          <img
            alt="Brand logo"
            src={theme === 'dark' ? WhcLogoWhite : WhcLogoBlack}
            height={30}
            style={{ lineHeight: "24px", }}
            className="brand-dark-logo ant-mx-auto"
          />
          <img
            alt="Brand sm logo"
            src={theme === 'dark' ? WhcLogoWhite : WhcLogoBlack}
            height={24}
            style={{ lineHeight: "24px" }}
            className="brand-sm-logo ant-mx-auto"
          />
          <StyledCollapsedButton
            themecolor={themecolor}
            type="link"
            onClick={toggleCollapsed}
          >
            {collapsed ? <ChevronRight /> : <ChevronLeft />}
          </StyledCollapsedButton>
        </StyleBrandLogo>
        <StyleSimpleBar>
          <Menu
            selectedKeys={[activatedItem]}
            mode="inline"
            theme="light"
            items={items}
            collapsedWidth="100"
            onClick={({ key }) => toggleActivation(key)}
          ></Menu>
        </StyleSimpleBar>
        <StyleSidebarFooter className={collapsed ? "collapsed" : ""}>
          <Popover
            placement="topLeft"
            content={getProfileContentPopover(userProfile?.id, userProfile?.profile?.username)}
            trigger={["click"]}
          >
            <div className="sidebar-user">
              <UserCircle2 size={20} />
              <Text className="sidebar-user-name">{userProfile?.profile?.username}</Text>
            </div>
          </Popover>
          <div className="sidebar-theme-toggle" onClick={handleToggleMode}>
            {darkMode === "dark" ? <Moon size={20} /> : <Sun size={20} />}
          </div>
        </StyleSidebarFooter>
      </StyleSider>
    </React.Fragment>
  );
};

export default withRouter(SidebarLayout);
